import Box from '@mui/material/Box';
import Chip, { ChipProps } from '@mui/material/Chip';
import { CatenaLinkedFleetStatus } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { Link } from 'react-router-dom';

import actionsColumn from '~components/DataTable/actionsColumn';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { CatenaLinkedFleet } from '~hooks/useCatena/models';
import { ROUTES as routes } from '~router/routePaths';

export type CatenaLinkedFleetRow = {
  id: string;
  fleet: CatenaLinkedFleet;
  onDisconnect: () => void;
};

const STATUS_CHIP_COLOR: Record<CatenaLinkedFleetStatus, ChipProps['color']> = {
  connected: 'success',
  pending: 'warning',
  expired: 'warning',
  declined: 'error',
  disconnected: 'default',
};

const vehiclesRoute = `/${routes.settings.base}/${routes.settings.integrations.base}/${routes.settings.integrations.catena.base}/${routes.settings.integrations.catena.vehicles}`;

export const statusColDef: DataTableColumn<CatenaLinkedFleetRow> = {
  field: 'status',
  headerName: t('administration.integration.catena.grid.status'),
  renderCell: (row) => {
    const status = row.fleet.status;

    return (
      <Chip
        size="small"
        color={status ? STATUS_CHIP_COLOR[status] : 'default'}
        label={t(`administration.integration.catena.status.${status ?? 'unknown'}`)}
        variant="outlined"
      />
    );
  },
};

export const invitationColDef: DataTableColumn<CatenaLinkedFleetRow> = {
  field: 'invitation',
  headerName: t('administration.integration.catena.grid.invitation'),
  renderCell: (row) => {
    const { magicLinkUrl, status } = row.fleet;

    if (magicLinkUrl && status === 'pending') {
      return (
        <PseudoLink
          title={t('administration.integration.catena.cta.open_invitation')}
          action={() => window.open(magicLinkUrl, '_blank', 'noopener,noreferrer')}
        />
      );
    }

    return <>—</>;
  },
};

export const actionsColDef: DataTableColumn<CatenaLinkedFleetRow> = actionsColumn(
  (row) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      {row.fleet.isConnected && (
        <SmallButton color="brandV2Yellow" component={Link} to={vehiclesRoute}>
          {t('administration.integration.catena.cta.view_vehicles')}
        </SmallButton>
      )}
      <SmallButton color="secondary" variant="outlined" onClick={row.onDisconnect}>
        {t('administration.integration.catena.cta.disconnect')}
      </SmallButton>
    </Box>
  ),
);
